import { formatJobAssignment, parseStatusNotes } from '@/common/application/utils/parseStatusNotes';
import { Add, Close, Warning } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import tid from '@/common/infrastructure/config/tid';

export interface UnavailableResource {
  id: number;
  name: string;
  role?: string | null;
  status: 'BOOKED' | 'OUT_OF_OFFICE' | 'OUT_OF_SERVICE' | 'BANNED';
  statusNotes?: string | null;
}

interface ResourceUnavailabilityWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  unavailableResources?: UnavailableResource[];
  resourceType?: 'crew' | 'equipment';
  currentJobId?: number;
}

export const ResourceUnavailabilityWarningModal = ({
  open,
  onClose,
  onConfirm,
  confirmText = 'Add Resource',
  cancelText = 'Edit Selection',
  unavailableResources,
  resourceType,
  currentJobId
}: ResourceUnavailabilityWarningModalProps) => {
  // Extract repeated status checks
  const hasBooked = unavailableResources?.some((r) => r.status === 'BOOKED');
  const hasOutOfOffice = unavailableResources?.some((r) => r.status === 'OUT_OF_OFFICE');
  const hasOutOfService = unavailableResources?.some((r) => r.status === 'OUT_OF_SERVICE');
  const hasBanned = unavailableResources?.some((r) => r.status === 'BANNED');

  // Helper function to get the correct resource type title (singular or plural)
  const getResourceTypeTitle = (isPlural: boolean) => {
    if (resourceType === 'crew') {
      return isPlural ? 'employees' : 'employee';
    }
    return 'equipment'; // equipment is uncountable, same for singular and plural
  };

  const getStatusTitle = () => {
    // Since we now show the modal individually, we can be more specific
    if (unavailableResources?.length === 1) {
      const resource = unavailableResources?.[0];
      if (!resource) return 'Resource Unavailable';

      switch (resource.status) {
        case 'BOOKED':
          return 'Resource Already Booked';
        case 'OUT_OF_OFFICE':
          return 'Resource Out of Office';
        case 'OUT_OF_SERVICE':
          return 'Resource Out of Service';
        case 'BANNED':
          return 'Resource Banned';
        default:
          return 'Resource Unavailable';
      }
    }

    // Fallback for multiple resources (shouldn't happen with current implementation)
    if (hasBooked && hasOutOfOffice) {
      return 'Resources Unavailable';
    }
    if (hasBooked) {
      return 'Resource Already Booked';
    }
    if (hasOutOfOffice) {
      return 'Resource Out of Office';
    }
    if (hasOutOfService) {
      return 'Resource Out of Service';
    }
    if (hasBanned) {
      return 'Resource Banned';
    }
    return resourceType && unavailableResources ? 'Resource Unavailable' : 'Resources Unavailable';
  };

  const getStatusMessage = () => {
    const resourceTypeTitle = getResourceTypeTitle(false);
    // For equipment, always use "is" since it's uncountable
    const verb = resourceType === 'crew' ? 'are' : 'is';

    // Since we now show the modal individually, we can be more specific
    if (unavailableResources?.length === 1) {
      const resource = unavailableResources?.[0];
      if (!resource) return `The following ${resourceTypeTitle} is unavailable:`;

      switch (resource.status) {
        case 'BOOKED':
          return `The following ${resourceTypeTitle} is already booked for the selected dates:`;
        case 'OUT_OF_OFFICE':
          return `The following ${resourceTypeTitle} is out of office for the selected dates:`;
        case 'OUT_OF_SERVICE':
          return `The following ${resourceTypeTitle} is out of service:`;
        case 'BANNED':
          return `The following ${resourceTypeTitle} is banned:`;
        default:
          return `The following ${resourceTypeTitle} is unavailable:`;
      }
    }

    // Fallback for multiple resources (shouldn't happen with current implementation)
    if (hasBooked && hasOutOfOffice) {
      return `The following ${getResourceTypeTitle(true)} ${verb} unavailable for the selected dates:`;
    }
    if (hasBooked) {
      return `The following ${resourceTypeTitle} is already booked for the selected dates:`;
    }
    if (hasOutOfOffice) {
      return `The following ${resourceTypeTitle} is out of office for the selected dates:`;
    }
    if (hasOutOfService) {
      return `The following ${resourceTypeTitle} is out of service:`;
    }
    if (hasBanned) {
      return `The following ${getResourceTypeTitle(true)} ${verb} banned:`;
    }
    return resourceType && unavailableResources && `The following ${getResourceTypeTitle(true)} ${verb} unavailable:`;
  };

  const formatResourceDisplay = (resource: UnavailableResource) => {
    const roleText = resource.role ? ` - ${resource.role}` : '';
    return `${resource.name}${roleText}`;
  };

  const formatStatusDescription = (resource: UnavailableResource) => {
    const statusNotes = resource.statusNotes;

    if (!statusNotes) {
      switch (resource.status) {
        case 'BOOKED':
          return 'Already assigned to another job';
        case 'OUT_OF_OFFICE':
          return 'Out of office';
        case 'OUT_OF_SERVICE':
          return 'Out of service';
        case 'BANNED':
          return 'Banned from job assignments';
        default:
          return 'Unavailable';
      }
    }

    // Parse status notes using the utility function
    const parsedNotes = parseStatusNotes(statusNotes, currentJobId);

    // If no assignments found, return the original status notes
    if (parsedNotes.filteredAssignments.length === 0) {
      return statusNotes;
    }

    // If only one assignment remains after filtering, return it as a simple string
    if (parsedNotes.filteredAssignments.length === 1) {
      const assignment = parsedNotes.filteredAssignments[0];
      if (assignment) {
        return formatJobAssignment(assignment);
      }
    }

    // For multiple assignments, create a more readable format
    // Use the parsed assignments from the utility (already filtered for currentJobId)
    const formattedAssignments = parsedNotes.filteredAssignments.map(formatJobAssignment);

    if (formattedAssignments.length > 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {formattedAssignments.map((job, index) => (
            <Typography
              key={index}
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: '1.25rem'
              }}
            >
              • {job}
            </Typography>
          ))}
        </Box>
      );
    }

    // Fallback to the original parsed notes if manual parsing fails
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {parsedNotes.filteredAssignments.map((assignment, index) => (
          <Typography
            key={index}
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '1.25rem'
            }}
          >
            • {formatJobAssignment(assignment)}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '30rem',
          padding: 4.5,
          boxShadow: (theme) => theme.shadows[2],
          borderRadius: 1
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
          fontWeight: 600,
          fontSize: '1.5rem',
          lineHeight: '2rem',
          color: 'text.primary',
          padding: 0,
          marginBottom: 3,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          {...tid('common:resource-unavailability-warning-modal:close-button')}
        >
          <Close />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              backgroundColor: 'warning.light',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Warning sx={{ color: 'primary.contrastText', fontSize: 29.4 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '1.5rem',
              lineHeight: '2rem',
              color: 'text.primary'
            }}
          >
            {getStatusTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, marginBottom: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {unavailableResources && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                color: 'text.primary'
              }}
            >
              {getStatusMessage()}
            </Typography>
          )}

          {unavailableResources && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {unavailableResources?.map((resource) => (
                <Box
                  key={resource.id}
                  sx={{
                    backgroundColor: 'grey.50',
                    padding: 1.5,
                    borderRadius: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem'
                    }}
                  >
                    • {formatResourceDisplay(resource)}
                  </Typography>
                  {typeof formatStatusDescription(resource) === 'string' ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        marginLeft: 2
                      }}
                    >
                      {formatStatusDescription(resource) as string}
                    </Typography>
                  ) : (
                    <Box sx={{ marginLeft: 2 }}>{formatStatusDescription(resource) as React.ReactNode}</Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {!hasBanned && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                color: 'text.primary'
              }}
            >
              Would you like to add them anyway?
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: 1,
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            padding: '6px 8px',
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: '1.375rem',
            textTransform: 'uppercase',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
          {...tid('common:resource-unavailability-warning-modal:cancel-button')}
        >
          {cancelText}
        </Button>
        {!hasBanned && (
          <Button
            onClick={onConfirm}
            variant="contained"
            color="primary"
            startIcon={<Add />}
            sx={{
              padding: '8px 22px',
              backgroundColor: 'primary.main',
              boxShadow: (theme) => theme.shadows[2],
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.375rem',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
            {...tid('common:resource-unavailability-warning-modal:confirm-button')}
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
